/**
 * hubspot.js
 * ----------
 * Handles all communication between the React component and the
 * Cloudflare Worker that posts data to HubSpot.
 *
 * This file does NOT talk to HubSpot directly. The HubSpot private app
 * token must never be exposed in client-side code. Instead, this file
 * sends a single POST request to the Cloudflare Worker endpoint, which
 * holds the token securely in an environment variable and makes the
 * actual HubSpot API calls server-side.
 *
 * WHAT GETS SENT:
 *   - contact        : Standard HubSpot contact fields + all cawood_ custom properties
 *   - pdfBase64      : The generated PDF as a base64 string
 *   - pdfFilename    : Filename for the attachment on the HubSpot record
 *   - note           : Plain-text note body for the contact timeline
 *   - analyticsData  : Anonymous scoring data for D1 database (no name or email)
 *
 * cawood_submission_token is NOT sent from here — it is generated server-side
 * in the worker and injected directly into the HubSpot contact upsert.
 *
 * WORKER ENDPOINT:
 *   Set REACT_APP_WORKER_ENDPOINT in your .env file.
 *   During development, replace with your Cloudflare Worker preview URL.
 *
 * ERROR HANDLING:
 *   submitToHubSpot() returns a result object rather than throwing.
 *   The calling component (ResultsStep.jsx) handles success/failure states.
 *
 * Connected files:
 *   - ResultsStep.jsx  calls submitToHubSpot() on mount
 *   - scoring.js       provides buildHubSpotNote() and buildCombinedProfile()
 *   - worker.js        (Cloudflare) receives and processes this payload
 */

// ─── Configuration ────────────────────────────────────────────────────────────

const WORKER_ENDPOINT = process.env.REACT_APP_WORKER_ENDPOINT
  || "https://cawood-assessment.YOUR-SUBDOMAIN.workers.dev/submit";

// ─── Main export: submitToHubSpot ────────────────────────────────────────────

/**
 * submitToHubSpot()
 * -----------------
 * Sends assessment results and the generated PDF to the Cloudflare Worker.
 * The worker creates/updates the HubSpot contact, attaches the PDF, posts
 * a note to the contact timeline, and writes anonymous data to D1.
 *
 * Scoring fields are included in the contact payload so they land directly
 * on the HubSpot contact record — visible in HubSpot lists, filters and views.
 *
 * cawood_submission_token is NOT included here. The worker generates it
 * server-side and injects it into the upsert directly.
 *
 * @param {Object} params
 * @param {Object} params.results    - Full results object from calculateResults()
 * @param {string} params.pdfBase64  - The generated PDF as a base64-encoded string
 * @param {string} params.noteText   - Plain-text note from buildHubSpotNote()
 *
 * @returns {Promise<{ success: boolean, submissionToken?: string, error?: string }>}
 */
export async function submitToHubSpot({ results, pdfBase64, noteText }) {

  const { respondent, focusDomainId, combinedProfile, domains } = results;

  // Build a domain score lookup — { D1: 3.2, D2: 4.1, ... }
  const scoreMap = {};
  for (const d of domains) {
    scoreMap[d.id] = d.displayScore;
  }

  // Weakest domains pair — stored as "D1, D3" for readability in HubSpot
const weakestPair = combinedProfile?.weakPairKey
  ? combinedProfile.weakPairKey.replace("_", ", ")
  : "";

  // ── Build the contact payload ─────────────────────────────────────────────
  const contactPayload = {
    // Standard HubSpot fields
    firstname:               respondent.firstName,
    lastname:                respondent.lastName,
    email:                   respondent.email,
    company:                 respondent.organisation,
    numemployees:            respondent.headcount,
    industry:                respondent.sector,

    // Cawood custom properties — scoring data
    cawood_primary_concern:  focusDomainId,
    cawood_avg_score:        results.averageScore,
    cawood_profile:          combinedProfile?.profileName  ?? "",
    cawood_d1_score:         scoreMap["D1"] ?? "",
    cawood_d2_score:         scoreMap["D2"] ?? "",
    cawood_d3_score:         scoreMap["D3"] ?? "",
    cawood_d4_score:         scoreMap["D4"] ?? "",
    cawood_d5_score:         scoreMap["D5"] ?? "",
    cawood_d6_score:         scoreMap["D6"] ?? "",
    cawood_strongest_domain: results.strongestDomain?.id  ?? "",
    cawood_weakest_domains:  weakestPair,
    // cawood_submission_token — set by the worker, not sent from client
  };

  // ── Build PDF filename ────────────────────────────────────────────────────
  const safeOrgName = respondent.organisation
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  const dateStamp   = new Date().toISOString().split("T")[0];
  const pdfFilename = `cawood-assessment-${safeOrgName}-${dateStamp}.pdf`;

  // ── Assemble request body ─────────────────────────────────────────────────
  const requestBody = {
    contact:  contactPayload,
    pdfBase64,
    pdfFilename,
    note:     noteText,
    resultsJson: results,
    analyticsData: {
      sector:           respondent.sector,
      headcount:        respondent.headcount,
      focus_domain:     focusDomainId,
      avg_score:        results.averageScore,
      strongest_domain: results.strongestDomain?.id,
      weakest_domain:   results.weakestDomain?.id,
      schema_version:   "1.0",
      domains:          domains.map(d => ({
        id:          d.id,
        raw:         d.rawScore,
        score:       d.displayScore,
        level:       d.level,
        gap:         d.perceptionDelta,
        gap_flagged: d.perceptionFlagged ? 1 : 0,
      })),
      questions:        results.questionSnapshots ?? [],
    },
  };

  // ── Send to Cloudflare Worker ─────────────────────────────────────────────
  try {
    const response = await fetch(WORKER_ENDPOINT, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify(requestBody),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      console.error("HubSpot submission failed:", data.error ?? response.statusText);
      return { success: false, error: data.error ?? "Submission failed. Please try again." };
    }

    // Return the submission token so ResultsStep can store it in sessionStorage
    return { success: true, submissionToken: data.submissionToken };

  } catch (err) {
    console.error("Network error submitting to HubSpot:", err);
    return {
      success: false,
      error: "We couldn't save your results right now. Your PDF has still been generated — please download it.",
    };
  }
}